import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, Upload, Download, Trash2, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { fiscalidadAPI } from '../api';

const MODELOS = ['303', '130', '111', '190', '390', '349', '100', '347', 'Otro'];
const TIPOS = [
  { valor: 'presentacion', etiqueta: 'Justificante de presentación' },
  { valor: 'pago', etiqueta: 'Justificante de pago (NRC)' },
  { valor: 'otro', etiqueta: 'Otro documento' },
];

const kb = (v) => (v ? `${Math.max(1, Math.round(v / 1024))} KB` : '');

export const JustificantesFiscales = ({ ejercicio, facturadorId }) => {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [modelo, setModelo] = useState('303');
  const [trimestre, setTrimestre] = useState('3');
  const [tipo, setTipo] = useState('presentacion');
  const [notas, setNotas] = useState('');
  const inputArchivo = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fiscalidadAPI.getJustificantes(ejercicio, {}, facturadorId);
      setLista(r.data.justificantes || []);
    } finally {
      setCargando(false);
    }
  }, [ejercicio, facturadorId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (archivo) => {
    if (!archivo) return;
    setSubiendo(true);
    try {
      await fiscalidadAPI.subirJustificante(ejercicio, {
        modelo, trimestre: trimestre === 'anual' ? null : Number(trimestre), tipo, notas,
      }, archivo, facturadorId);
      toast({ title: `Justificante del modelo ${modelo} guardado` });
      setNotas('');
      if (inputArchivo.current) inputArchivo.current.value = '';
      cargar();
    } catch (e) {
      toast({ title: 'No se ha podido guardar el archivo', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setSubiendo(false);
    }
  };

  const descargar = async (j) => {
    const r = await fiscalidadAPI.descargarJustificante(ejercicio, j.id);
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = j.nombre_archivo;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const borrar = async (j) => {
    if (!window.confirm(`¿Borrar «${j.nombre_archivo}»? No se puede deshacer.`)) return;
    await fiscalidadAPI.borrarJustificante(ejercicio, j.id);
    toast({ title: 'Justificante borrado' });
    cargar();
  };

  return (
    <div className="space-y-4" data-testid="justificantes-fiscales">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Guardar el justificante de una presentación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="text-[11px] text-slate-500">Modelo</label>
              <select className="w-full h-9 text-sm border rounded px-2" value={modelo}
                      onChange={(e) => setModelo(e.target.value)} data-testid="justif-modelo">
                {MODELOS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Periodo</label>
              <select className="w-full h-9 text-sm border rounded px-2" value={trimestre}
                      onChange={(e) => setTrimestre(e.target.value)} data-testid="justif-periodo">
                <option value="1">1T</option>
                <option value="2">2T</option>
                <option value="3">3T</option>
                <option value="4">4T</option>
                <option value="anual">Anual</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Tipo</label>
              <select className="w-full h-9 text-sm border rounded px-2" value={tipo}
                      onChange={(e) => setTipo(e.target.value)} data-testid="justif-tipo">
                {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.etiqueta}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Nota (opcional)</label>
              <Input value={notas} onChange={(e) => setNotas(e.target.value)}
                     placeholder="Nº de justificante, NRC..." className="h-9 text-sm"
                     data-testid="justif-notas" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input ref={inputArchivo} type="file" className="hidden"
                   accept=".pdf,.png,.jpg,.jpeg,.xml,.csv,.zip"
                   onChange={(e) => subir(e.target.files?.[0])} data-testid="justif-input-archivo" />
            <Button size="sm" disabled={subiendo} onClick={() => inputArchivo.current?.click()}
                    data-testid="justif-subir-btn">
              {subiendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Subir PDF del modelo {modelo}
            </Button>
            <span className="text-[11px] text-slate-500">
              PDF, imagen, XML, CSV o ZIP · hasta 10 MB · se guarda en la base de datos, así entra
              en la copia de seguridad
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Justificantes guardados de {ejercicio}</CardTitle>
        </CardHeader>
        <CardContent>
          {cargando && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>}
          {!cargando && lista.length === 0 && (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="justif-vacio">
              Todavía no has guardado ningún justificante de {ejercicio}.
            </p>
          )}
          {!cargando && lista.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="justif-tabla">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-slate-500">
                    <th className="text-left px-3 py-2">Modelo</th>
                    <th className="text-left px-3 py-2">Periodo</th>
                    <th className="text-left px-3 py-2">Tipo</th>
                    <th className="text-left px-3 py-2">Archivo</th>
                    <th className="text-left px-3 py-2">Nota</th>
                    <th className="text-left px-3 py-2">Guardado</th>
                    <th className="text-right px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((j) => (
                    <tr key={j.id} className="border-b last:border-0" data-testid={`justif-fila-${j.id}`}>
                      <td className="px-3 py-2 font-semibold">{j.modelo}</td>
                      <td className="px-3 py-2">{j.periodo}</td>
                      <td className="px-3 py-2">
                        <Badge variant={j.tipo === 'pago' ? 'secondary' : 'outline'}>
                          {TIPOS.find((t) => t.valor === j.tipo)?.etiqueta || j.tipo}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3 text-slate-400" />
                          {j.nombre_archivo} <span className="text-slate-400">{kb(j.tamano)}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">{j.notas || ''}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {j.created_at ? j.created_at.slice(0, 10).split('-').reverse().join('/') : ''}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => descargar(j)}
                                data-testid={`justif-descargar-${j.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => borrar(j)}
                                data-testid={`justif-borrar-${j.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JustificantesFiscales;
